import React, { useState, useEffect } from 'react';
import { AlertCircle, KeyRound } from 'lucide-react';
import Modal from './Modal';
import api from '../config/api';
import { showNotification } from './GlobalNotification';
import { useLanguage } from '../context/LanguageContext';

function ChangePasswordModal({ isOpen, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setError('');
    }
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.currentPassword || !form.newPassword) {
      setError(t('Enter your current and new password'));
      return;
    }

    if (form.newPassword.length < 6) {
      setError(t('The new password must be at least 6 characters'));
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError(t('The new passwords do not match'));
      return;
    }

    setSubmitting(true);

    try {
      await api.put('/api/auth/me/password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });

      showNotification('success', t('Password changed successfully.'));
      onClose();
    } catch (submitError) {
      setError(submitError.response?.data?.message || t('Could not change the password.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('Change Password')} size="md">
      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Current password')}</label>
          <input
            type="password"
            name="currentPassword"
            value={form.currentPassword}
            onChange={handleChange}
            className="input-field"
            autoComplete="current-password"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('New password')}</label>
          <input
            type="password"
            name="newPassword"
            value={form.newPassword}
            onChange={handleChange}
            className="input-field"
            autoComplete="new-password"
          />
          <p className="text-xs text-gray-500 mt-1">{t('At least 6 characters.')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Confirm new password')}</label>
          <input
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            className="input-field"
            autoComplete="new-password"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">
            {t('Cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <KeyRound className="h-4 w-4" />
            {submitting ? t('Saving...') : t('Change Password')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ChangePasswordModal;
